import { Box, Stack, Typography } from '@mui/material';
import Image from 'next/image';
import { Icon } from '@/units/index';
import { NoteCardProps } from '../types';
import { NoteActions } from './actions';
import { DateRange } from './date-range';
import { ReporterAssignee } from './reporter-assignee';
import { ActionButton } from './ui/action-button';
import { PriorityIcon } from './ui/priority-icon';
import { FAIcon } from '@packages/ui-components';

export function NoteCard({
  assignee,
  fromDate,
  toDate,
  description,
  reporter,
  title,
  priority,
  onChatClick,
  onClick,
  onMoveToTagClick,
  onMoveToBacklog,
  secondaryDescription,
  secondaryTitle,
  onDelete,
  onEdit,
  onStatusChange,
  isNew,
  imageSrc,
  isCompleted,
  hasNewMessages,
  indicatorColor,
  indicatorStyle = 'side',
  sx,
  reverts,
  ...props
}: NoteCardProps) {
  return (
    <Stack
      {...props}
      direction="column"
      gap="5px"
      justifyContent="space-between"
      sx={{
        position: 'relative',
        cursor: 'pointer',
        borderRadius: '12px',
        boxShadow: `0px 0px 0px 2px ${
          indicatorStyle === 'around' ? indicatorColor ?? '#EAECF0' : '#EAECF0'
        } inset`,
        p: '10px',
        width: '322px',
        borderLeft:
          indicatorStyle === 'side' && indicatorColor ? `2px solid ${indicatorColor}` : null,
        containerType: 'inline-size',
        userSelect: 'none',
        opacity: isCompleted ? 0.7 : 1,
        backgroundColor: isCompleted ? '#EAECF0' : '#fff',
        ...sx,
      }}
      onClick={() => onClick?.()}
    >
      <Stack direction="column" gap="5px">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{
            display: imageSrc ? 'flex' : 'none',
            '@container (max-width: 245px)': {
              display: 'flex',
            },
          }}
        >
          {imageSrc ? (
            <Box
              position="relative"
              sx={{ aspectRatio: 1, width: '40px', overflow: 'hidden', borderRadius: '4px' }}
            >
              <Image src={imageSrc} alt={title} fill objectFit="cover" />
            </Box>
          ) : (
            <Box />
          )}

          <NoteActions
            isCompleted={isCompleted}
            onDelete={onDelete}
            onEdit={onEdit}
            onStatusChange={onStatusChange}
            onMoveToBacklog={onMoveToBacklog}
          />
        </Stack>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography
              sx={{
                overflow: 'hidden',
                color: 'var(--Gray-900, #101828)',
                textOverflow: 'ellipsis',
                fontFamily: 'Inter',
                fontSize: '13px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '14px',
                width: '100%',
                '@container (max-width: 245px)': {
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 3,
                  whiteSpace: 'normal',
                },
                '@container (min-width: 246px)': {
                  whiteSpace: 'nowrap',
                },
              }}
            >
              {title}
            </Typography>

            <Box
              sx={{
                '@container (max-width: 245px)': {
                  display: 'none',
                },
              }}
            >
              {!imageSrc && (
                <NoteActions
                  isCompleted={isCompleted}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onStatusChange={onStatusChange}
                  onMoveToBacklog={onMoveToBacklog}
                />
              )}
            </Box>
          </Stack>

          {description ? (
            <Typography
              sx={{
                color: '#000',
                fontFamily: 'Inter',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '18px',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                mt: 0,
                '@container (max-width: 245px)': {
                  display: 'none',
                },
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: description }} />
            </Typography>
          ) : null}
        </Box>

        {secondaryTitle || secondaryDescription ? (
          <Stack>
            {secondaryDescription ? (
              <Box
                sx={{
                  borderRadius: '4px',
                  backgroundColor: '#F2F4F7',
                  padding: '2px 4px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: 'fit-content',
                  maxWidth: '100%',
                }}
              >
                <Typography
                  sx={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: 'var(--Gray-700, #344054)',
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '14px',
                    wordBreak: 'break-all',
                    maxWidth: '100%',
                  }}
                  dangerouslySetInnerHTML={{ __html: secondaryDescription }}
                />
              </Box>
            ) : null}
            {secondaryTitle ? (
              <Typography
                sx={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: 'var(--Gray-500, #667085)',
                  fontFamily: 'Inter',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '18px',
                }}
              >
                {secondaryTitle}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {fromDate ? <DateRange fromDate={fromDate} toDate={toDate} /> : null}
      </Stack>

      <Stack direction="row" justifyContent="space-between">
        {reporter ? (
          <Box
            sx={{
              '@container (max-width: 220px)': {
                '& .assignee-container': {
                  display: 'none',
                },
              },
            }}
          >
            <ReporterAssignee reporter={reporter} assignee={assignee} />
          </Box>
        ) : (
          <Box />
        )}

        <Stack direction="row" gap="5px">
          {typeof reverts === 'number' && reverts > 0 && (
            <Box
              sx={{
                display: 'flex',
                padding: '2px 6px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
                borderRadius: '4px',
                border: '1px solid var(--Gray-100, #F2F4F7)',
                height: '22px',
              }}
            >
              <Icon.Repeat sx={{ stroke: '#C4320A', fontSize: '12px' }} />

              <Typography
                sx={{
                  color: 'var(--Orange-700, #C4320A)',
                  textAlign: 'center',
                  fontFamily: 'Inter',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '18px',
                }}
              >
                {reverts}
              </Typography>
            </Box>
          )}

          {isNew ? (
            <Box
              sx={{
                backgroundColor: '#FFF6ED',
                height: '22px',
                padding: '2px 8px',
                borderRadius: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                '@container (max-width: 220px)': {
                  display: 'none',
                },
              }}
            >
              <Typography
                sx={{
                  color: '#C4320A',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '18px',
                  fontFamily: 'Inter',
                  textAlign: 'center',
                }}
              >
                New
              </Typography>
            </Box>
          ) : null}

          {typeof priority === 'number' ? (
            <ActionButton onClick={(e) => e.stopPropagation()}>
              <PriorityIcon priority={priority} sx={{ fontSize: '14px' }} />
            </ActionButton>
          ) : null}

          <ActionButton onClick={() => onChatClick?.()} sx={{ position: 'relative' }}>
            <Icon.CircleChat sx={{ fontSize: '14px', strokeWidth: '1.2px' }} />

            {hasNewMessages ? (
              <Box
                sx={{
                  position: 'absolute',
                  width: '6px',
                  aspectRatio: 1,
                  borderRadius: '50%',
                  bottom: '4px',
                  right: '4px',
                  background: 'red',
                }}
              />
            ) : null}
          </ActionButton>

          {onMoveToTagClick ? (
            <ActionButton
              onClick={() => onMoveToTagClick()}
              sx={{
                '@container (max-width: 220px)': {
                  display: 'none',
                },
              }}
            >
              <FAIcon
                icon="faLocationCrosshairs"
                size={14}
                color="#667085"
              />
            </ActionButton>
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
}
